import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { Route } from "@/enums/route";
import { toast } from "@/lib/toast";
import { useCustomerAccount } from "@/providers/CustomerAccountProvider";
import type { FilteredPricingEngineRequest } from "@generated/types";

/**
 * Custom hook to automatically switch customer accounts when navigating to a pricing request
 * that belongs to a different account. This ensures deep links to payment/exchange URLs
 * always load the correct customer account.
 *
 * @param pricingRequestId - The pricing request ID from the URL params
 * @param isFirstStep - Whether we're on the first step (input page) - skip auto-switching there
 * @param pricingRequestData - Pricing request data (passed from PricingProvider to avoid duplicate query)
 */
export function useAutoAccountSwitch(
  pricingRequestId: string | undefined,
  isFirstStep: boolean,
  pricingRequestData: FilteredPricingEngineRequest | undefined
) {
  const router = useRouter();
  const tCommon = useTranslations("common");
  const {
    customerAccount,
    customerAccounts,
    changeCustomerAccount,
    isSwitchingAccount,
  } = useCustomerAccount();

  // Track if we've already processed this pricing request to avoid re-running
  const processedPricingRequestRef = useRef<string | null>(null);

  useEffect(() => {
    // Only validate when we have pricing request data and we're not on the first step
    if (!pricingRequestData || isFirstStep || !customerAccounts?.length) {
      return;
    }

    // Skip if we're currently switching accounts (avoid race conditions)
    if (isSwitchingAccount) {
      return;
    }

    // Skip if we've already processed this pricing request
    if (processedPricingRequestRef.current === pricingRequestId) {
      return;
    }

    const pricingRequestCustomerAccountId = pricingRequestData.customerAccount;

    // Find if this pricing request belongs to any of the user's accounts
    const matchingAccount = customerAccounts.find(
      (account) =>
        account.customerAccount?.id === pricingRequestCustomerAccountId
    );

    if (!matchingAccount) {
      // No matching account found - redirect to access denied page
      router.replace(Route.AccessDenied);
      return;
    }

    // Check if it's a different account than the current one
    if (matchingAccount.id !== customerAccount?.id) {
      // Mark this pricing request as processed before switching
      processedPricingRequestRef.current = pricingRequestId || null;

      // Switch to the correct account
      const accountName =
        matchingAccount.customerAccount?.name ||
        matchingAccount.customerAccount?.reference ||
        "Unknown Account";

      changeCustomerAccount(matchingAccount.id!);
      toast.info(tCommon("accountSwitched", { accountName }));
    } else {
      // Account matches - mark as processed to avoid re-checking
      processedPricingRequestRef.current = pricingRequestId || null;
    }
    // Intentionally omit changeCustomerAccount and other deps to only run on mount/pricing request change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pricingRequestData,
    pricingRequestId,
    isFirstStep,
    customerAccounts,
    isSwitchingAccount,
  ]);

  // Reset the processed ref when pricing request ID changes
  useEffect(() => {
    if (pricingRequestId !== processedPricingRequestRef.current) {
      processedPricingRequestRef.current = null;
    }
  }, [pricingRequestId]);
}
